import { Grid } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import Dialog from "components/Dialog";
import { GeoAutoCompletion } from "components/GeoAutoCompletion";
import { TextField } from "components/TextField";
import { isValidPhoneNumberFormat } from "config/constants";
import useText from "constants/text";
import { formatReceiver } from "domains/Receiver/Components/NewReceiver";
import { Address, Receiver } from "generated/graphql";
import { useCountryCode } from "hooks/useCurrentUser";
import React, { useMemo } from "react";
import "styled-components/macro";
import { ValidReceiver } from "utils";

const defaultAddress: Address = {
  address: "",
  place_id: "",
  additional_address: "",
};
const defaultValue: Receiver = {
  receiver_firstname: "",
  receiver_lastname: "",
  receiver_phone_number: "",
  receiver_entreprise: "",
  receiver_address: defaultAddress,
  receiver_specification_address: "",
};

export interface ReceiverProps {
  open: boolean;
  handleClose: () => void;
  // eslint-disable-next-line no-unused-vars
  handleNew(newReceiver: Receiver): void;
  current?: Receiver;
}
export default function NewReceiverModal({ open, handleClose, handleNew, current }: ReceiverProps) {
  const [formValues, setFormValues] = React.useState<Receiver>(defaultValue);
  const [isFormSubmited, setFormSubmited] = React.useState<boolean>(false);
  const { text } = useText();
  const countryCode = useCountryCode();

  React.useEffect(() => {
    if (open) {
      setFormValues(defaultValue);
    }
  }, [open]);

  React.useEffect(() => {
    if (current) {
      setFormValues({
        receiver_firstname: current.receiver_firstname,
        receiver_lastname: current.receiver_lastname,
        receiver_phone_number: current.receiver_phone_number,
        receiver_entreprise: current.receiver_entreprise,
        receiver_address: {
          address: current.receiver_address.address,
          place_id: current.receiver_address.place_id,
          additional_address: current.receiver_address.additional_address,
        },
        receiver_specification_address: current.receiver_specification_address,
      });
    }
  }, [current]);

  const handleInputChange = React.useCallback(
    (event: any) => {
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );

  const handleConfirm = () => {
    setFormSubmited(true);
    if (!isValidPhoneNumberFormat(formValues.receiver_phone_number)) return;
    if (
      formValues?.receiver_address?.place_id === undefined ||
      formValues?.receiver_address?.place_id === null ||
      formValues?.receiver_address?.place_id?.length === 0 ||
      formValues?.receiver_address?.address === undefined ||
      formValues?.receiver_address?.address === null ||
      formValues?.receiver_address?.address?.length === 0
    )
      return;

    // eslint-disable-next-line no-restricted-syntax
    for (const el in formValues)
      if (el !== "receiver_entreprise" && el !== "receiver_specification_address" && formValues[el]?.length === 0)
        return;

    handleNew(formatReceiver(formValues));

    setFormSubmited(false);
    setFormValues(defaultValue);
  };

  const handleCloseModal = () => {
    setFormSubmited(false);
    handleClose();
  };

  const isValidPhone = () => isValidPhoneNumberFormat(formValues.receiver_phone_number);

  const isFormValid = useMemo(() => ValidReceiver.isValidSync(formValues), [formValues]);
  return (
    <Dialog
      title={current ? text.all.edit_client : text.all.add_client}
      open={open}
      confirmButtonTitle={text.all.add}
      handleClose={handleCloseModal}
      handleConfirm={handleConfirm}
      maxWidth="lg"
      key="add receiver dialog"
      showBottomInfo
    >
      <Grid container spacing={4} style={{ padding: "54px 54px", height: 400 }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.all.first_name}
            name="receiver_firstname"
            value={formValues?.receiver_firstname}
            onChange={handleInputChange}
            error={isFormSubmited && formValues?.receiver_firstname?.length === 0}
            helperText={
              isFormSubmited && formValues?.receiver_firstname?.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.all.last_name}
            name="receiver_lastname"
            value={formValues?.receiver_lastname}
            onChange={handleInputChange}
            error={isFormSubmited && formValues?.receiver_lastname?.length === 0}
            helperText={
              isFormSubmited && formValues?.receiver_lastname?.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.all.entreprise}
            name="receiver_entreprise"
            value={formValues?.receiver_entreprise}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={6}>
          <PhoneField
            countryCode={countryCode}
            fullWidth
            label={text.all.phone_number}
            name="receiver_phone_number"
            value={formValues?.receiver_phone_number}
            onValueChange={(value) => {
              setFormValues({
                ...formValues,
                receiver_phone_number: value,
              });
            }}
            error={isFormSubmited && !isValidPhone()}
            helperText={isFormSubmited && !isValidPhone() ? `${text.all.this_field_is_required}` : ""}
            required
            type="tel"
          />
        </Grid>

        <Grid item xs={6}>
          <GeoAutoCompletion
            onChange={(values) => setFormValues((prev) => ({ ...prev, receiver_address: values }))}
            address={formValues?.receiver_address}
            error={
              isFormSubmited &&
              (formValues?.receiver_address?.place_id === undefined ||
                formValues?.receiver_address?.place_id === null ||
                formValues?.receiver_address?.address?.length === 0 ||
                formValues?.receiver_address?.address?.length === undefined)
            }
            helperText={
              isFormSubmited &&
              (formValues?.receiver_address?.place_id === undefined ||
                formValues?.receiver_address?.place_id === null ||
                formValues?.receiver_address?.address?.length === 0 ||
                formValues?.receiver_address?.address?.length === undefined)
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label={text.all.specification}
            name="receiver_specification_address"
            value={formValues?.receiver_specification_address}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
}
